/**
 * Matching of loose (non-eCTD-packaged) PDF files to canonical eCTD section
 * keys. Files are matched deterministically from section codes found anywhere
 * on the first PDF page (position-independent), combined with folder and
 * filename hints; files that stay unresolved can be classified with a single
 * batched LLM call (see classifyUnmatchedWithLlm).
 */
import { ParsedPdf } from "./parseZipPdfs";
import {
  EctdSection,
  ectdSections,
  findSectionByCode,
  findSectionByKey,
  getAncestors,
  getVariantRoot,
  isDescendantOf,
  normalizeSectionCode,
  titleKeywords,
} from "./ectd/ectdSections";
import { runChatOpenRouter } from "./openRouterClient";
import { RunPromptOptions } from "./aiCommon";

export type MatchConfidence = "high" | "medium" | "low";
export type MatchedBy =
  | "header"
  | "folder"
  | "filename"
  | "llm"
  | "manual"
  | "none";

export type EctdFileMatch = {
  file: ParsedPdf;
  /** Canonical section code (e.g. "3.2.S.4.2") or null when unresolved. */
  sectionCode: string | null;
  section?: EctdSection;
  /**
   * Variant name within the section's repeatable root (e.g. manufacturer
   * folder name "ceftriaxone-sodium-orchid" or "Orchid Pharma Ltd."). Empty
   * string = default/unnamed variant; undefined = section has no variant root.
   */
  variant?: string;
  confidence: MatchConfidence;
  matchedBy: MatchedBy;
  /** All section codes that were considered for this file. */
  candidates: string[];
  /** True when the document declares the section "not applicable". */
  declaredNotApplicable: boolean;
};

export type SectionRowStatus = "present" | "missing" | "not-applicable";

export type SectionRow = {
  section: EctdSection;
  files: EctdFileMatch[];
  status: SectionRowStatus;
};

export type VariantCoverage = {
  /** Repeatable DTD root key (e.g. "m3-2-s-drug-substance") or null for the shared column. */
  variantRootKey: string | null;
  /** Display name, e.g. "Drug Substance — ceftriaxone-sodium-orchid". */
  name: string;
  rows: SectionRow[];
};

export type EctdCompletenessReport = {
  variants: VariantCoverage[];
  unmatched: EctdFileMatch[];
  /** CTD modules detected in the uploaded set (e.g. [3]). */
  detectedModules: number[];
};

// ---------------------------------------------------------------------------
// First-page header scan (position-independent)
// ---------------------------------------------------------------------------

type HeaderCodeOccurrence = {
  code: string;
  offset: number;
  /** Code is at the start of its line (possibly after the word "Section"). */
  atLineStart: boolean;
  /** Code is directly preceded by the word "Section". */
  afterSectionWord: boolean;
};

const FIRST_PAGE_FALLBACK_CHARS = 3000;

/**
 * Leading markdown/decoration that vision transcriptions put in front of a
 * header ("**Section 3.2.P.8**", "## 3.2.P.8.1", "| 3.2.P.8.1 |", "- 3.2.P.8").
 */
const LINE_DECORATION_RE = /^[*#_|>\-\s]+/;

function getFirstPageText(file: ParsedPdf): string {
  if (typeof file.firstPageText === "string" && file.firstPageText.length > 0)
    return file.firstPageText;
  return (file.text || "").slice(0, FIRST_PAGE_FALLBACK_CHARS);
}

export function scanHeaderCodes(pageText: string): HeaderCodeOccurrence[] {
  const occurrences: HeaderCodeOccurrence[] = [];

  const record = (rawCode: string, offset: number): void => {
    // Guard against numeric contexts like "13.2..." or "0.3.3"
    const prev = offset > 0 ? pageText[offset - 1] : "";
    if (/[\d.]/.test(prev)) return;

    const code = normalizeSectionCode(rawCode);
    if (!code || !code.startsWith("3")) return;

    const lineStart = pageText.lastIndexOf("\n", offset - 1) + 1;
    const prefix = pageText
      .slice(lineStart, offset)
      .trim()
      .replace(LINE_DECORATION_RE, "")
      .replace(/[*_]+$/, "")
      .trim();
    // "Section 3.2.S.4 ..." at the start of a line is a header; a mid-sentence
    // "... provided in section 3.2.P.3.5" is a cross-reference and stays weak.
    const afterSectionWord = /^section$/i.test(prefix);
    const atLineStart = prefix === "" || afterSectionWord;

    occurrences.push({ code, offset, atLineStart, afterSectionWord });
  };

  // Module 3 codes with a part letter: 3.2.S / 3.2.P / 3.2.A / 3.2.R (+ digits)
  const lettered = /3\s*\.\s*2\s*\.?\s*([SPAR])((?:\s*\.\s*\d+)*)/gi;
  let match = lettered.exec(pageText);
  while (match) {
    record(`3.2.${match[1]}${match[2] || ""}`, match.index);
    match = lettered.exec(pageText);
  }

  // 3.3 Literature References
  const litRef = /3\s*\.\s*3(?!\s*\.?\s*[\dSPAR])/gi;
  match = litRef.exec(pageText);
  while (match) {
    record("3.3", match.index);
    match = litRef.exec(pageText);
  }

  return occurrences.sort((a, b) => a.offset - b.offset);
}

const codeSegments = (code: string): number => code.split(".").length;

const isCodeDescendant = (child: string, parent: string): boolean =>
  child !== parent && child.startsWith(`${parent}.`);

const codesRelated = (a: string, b: string): boolean =>
  a === b || isCodeDescendant(a, b) || isCodeDescendant(b, a);

type HeaderScanResult = {
  /** Shallowest strong code, e.g. "3.2.S.4". */
  group: string | null;
  /** Distinct strong codes that are strict descendants of group, in page order. */
  leafCandidates: string[];
  /** True when the page only contains mid-line (cross-reference-like) codes. */
  weakOnly: boolean;
  /** First offset of the chosen group code. */
  groupOffset: number;
};

export function analyzeHeader(pageText: string): HeaderScanResult {
  const occurrences = scanHeaderCodes(pageText);
  if (occurrences.length === 0) {
    return { group: null, leafCandidates: [], weakOnly: false, groupOffset: 0 };
  }

  const strong = occurrences.filter((o) => o.atLineStart || o.afterSectionWord);
  const pool = strong.length > 0 ? strong : occurrences;
  const weakOnly = strong.length === 0;

  // Group: shallowest code; prefer occurrences following the word "Section".
  const sectionWorded = pool.filter((o) => o.afterSectionWord);
  const groupPool = sectionWorded.length > 0 ? sectionWorded : pool;
  const group = groupPool.reduce((best, o) =>
    codeSegments(o.code) < codeSegments(best.code) ? o : best
  );

  const leafCandidates: string[] = [];
  strong
    .filter(
      (o) => o.offset >= group.offset && isCodeDescendant(o.code, group.code)
    )
    .forEach((o) => {
      if (!leafCandidates.includes(o.code)) leafCandidates.push(o.code);
    });

  return {
    group: group.code,
    leafCandidates,
    weakOnly,
    groupOffset: group.offset,
  };
}

// ---------------------------------------------------------------------------
// Folder and filename hints
// ---------------------------------------------------------------------------

/** Map a folder segment like "32s42-analyt-proc" or "33-lit-ref" to a section code. */
export function folderSegmentToCode(segment: string): string | null {
  const prefix = (segment.split("-")[0] || "").toLowerCase();
  const match = /^3(\d)?([spar])?(\d)?(\d)?$/.exec(prefix);
  if (!match) return null;
  const [, d1, letter, d2, d3] = match;
  let code = "3";
  if (d1) code += `.${d1}`;
  if (letter) code += `.${letter.toUpperCase()}`;
  if (d2) code += `.${d2}`;
  if (d3) code += `.${d3}`;
  return normalizeSectionCode(code);
}

function pathSegments(fileName: string): string[] {
  return fileName.replace(/\\/g, "/").split("/").filter(Boolean);
}

/** Deepest section code derivable from the directory path of the file. */
export function folderCodeFromPath(fileName: string): string | null {
  const dirs = pathSegments(fileName).slice(0, -1);
  let best: string | null = null;
  dirs.forEach((dir) => {
    const code = folderSegmentToCode(dir);
    if (code && (!best || codeSegments(code) > codeSegments(best))) {
      best = code;
    }
  });
  return best;
}

const tokenMatchesKeyword = (token: string, keyword: string): boolean => {
  if (token === keyword) return true;
  const len = Math.min(token.length, keyword.length);
  if (len < 3) return false;
  return token.slice(0, len) === keyword.slice(0, len);
};

const FILENAME_STOPWORDS = new Set(["and", "the", "for", "with"]);

function filenameTokens(fileName: string): string[] {
  const base = (pathSegments(fileName).pop() || "")
    .replace(/\.pdf$/i, "")
    // Drop manufacturer suffixes like "_Controlled by Orchid Pharma"
    .replace(/_?controlled by.*$/i, "");
  return base
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter((t) => t.length >= 3 && !FILENAME_STOPWORDS.has(t));
}

/**
 * Similarity between filename tokens and a section title: harmonic mean of
 * title-keyword recall and filename-token precision. Recall alone would let a
 * one-word title ("Manufacturers") score 1.0 against
 * "manuf-process-and-controls" on a single prefix match.
 */
function filenameTitleScore(tokens: string[], keywords: string[]): number {
  const matchedKeywords = keywords.filter((kw) =>
    tokens.some((token) => tokenMatchesKeyword(token, kw))
  ).length;
  const matchedTokens = tokens.filter((token) =>
    keywords.some((kw) => tokenMatchesKeyword(token, kw))
  ).length;
  if (matchedKeywords === 0 || matchedTokens === 0) return 0;
  const recall = matchedKeywords / keywords.length;
  const precision = matchedTokens / tokens.length;
  return (2 * precision * recall) / (precision + recall);
}

/**
 * Best section within `scope` whose title keywords match the filename.
 * Returns null unless a unique best match scores >= 0.5 (see
 * filenameTitleScore).
 */
export function filenameCodeFromScope(
  fileName: string,
  scope: EctdSection[]
): string | null {
  const tokens = filenameTokens(fileName);
  if (tokens.length === 0 || scope.length === 0) return null;

  let bestScore = 0;
  let bestSections: EctdSection[] = [];
  scope.forEach((section) => {
    const keywords = titleKeywords(section);
    if (keywords.length === 0) return;
    const score = filenameTitleScore(tokens, keywords);
    if (score > bestScore) {
      bestScore = score;
      bestSections = [section];
    } else if (score === bestScore && score > 0) {
      bestSections.push(section);
    }
  });

  if (bestScore >= 0.5 && bestSections.length === 1) {
    return bestSections[0].code;
  }
  return null;
}

const sectionAndDescendants = (root: EctdSection): EctdSection[] =>
  ectdSections.filter((s) => s.key === root.key || isDescendantOf(s, root));

/** Strict descendants of the section with the given code ([] if unknown). */
const descendantsOfCode = (code: string): EctdSection[] => {
  const root = findSectionByCode(code);
  if (!root) return [];
  return ectdSections.filter((s) => isDescendantOf(s, root));
};

// ---------------------------------------------------------------------------
// Variant detection
// ---------------------------------------------------------------------------

function variantFromPath(
  fileName: string,
  variantRoot: EctdSection
): string | null {
  const segments = pathSegments(fileName);
  // Directories only (last segment is the file itself)
  for (let i = 0; i < segments.length - 1; i += 1) {
    const code = folderSegmentToCode(segments[i]);
    if (code === variantRoot.code) {
      const next = segments[i + 1];
      const nextIsDirectory = i + 1 < segments.length - 1;
      // The next segment names the variant unless it is itself a section folder
      if (nextIsDirectory && folderSegmentToCode(next) === null) {
        return next;
      }
      return "";
    }
  }
  return null;
}

function variantFromPage(pageText: string): string | null {
  const match = /controlled\s+by\s*[:\-–]?\s*([^)\n]+)/i.exec(pageText);
  if (!match) return null;
  const name = match[1].trim().replace(/[.,;:]+$/, "");
  return name.length > 0 ? name : null;
}

function detectVariant(
  file: ParsedPdf,
  section: EctdSection
): string | undefined {
  const variantRoot = getVariantRoot(section);
  if (!variantRoot) return undefined;
  const fromPath = variantFromPath(file.fileName, variantRoot);
  if (fromPath !== null) return fromPath;
  const fromPage = variantFromPage(getFirstPageText(file));
  if (fromPage) return fromPage;
  return "";
}

// ---------------------------------------------------------------------------
// Not-applicable detection
// ---------------------------------------------------------------------------

const NOT_APPLICABLE_RE = /not\s+applicable/i;

function detectNotApplicable(pageText: string, codeOffset: number): boolean {
  // A genuine "whole section not applicable" page is essentially just the
  // header plus the declaration; longer pages that merely mention
  // "not applicable" for one sub-item must not be flagged.
  if (pageText.trim().length < 400 && NOT_APPLICABLE_RE.test(pageText)) {
    return true;
  }
  return NOT_APPLICABLE_RE.test(pageText.slice(codeOffset, codeOffset + 150));
}

// ---------------------------------------------------------------------------
// Main deterministic matcher
// ---------------------------------------------------------------------------

function matchSingleFile(file: ParsedPdf): EctdFileMatch {
  const pageText = getFirstPageText(file);
  const header = analyzeHeader(pageText);
  const folderCode = folderCodeFromPath(file.fileName);

  const candidates = new Set<string>();
  if (header.group) candidates.add(header.group);
  header.leafCandidates.forEach((c) => candidates.add(c));
  if (folderCode) candidates.add(folderCode);

  let sectionCode: string | null = null;
  let matchedBy: MatchedBy = "none";
  let confidence: MatchConfidence = "low";

  const groupSection = header.group
    ? findSectionByCode(header.group)
    : undefined;

  if (header.leafCandidates.length === 1) {
    // Single unambiguous leaf code in the header
    [sectionCode] = header.leafCandidates;
    matchedBy = "header";
    confidence =
      !folderCode || codesRelated(folderCode, sectionCode) ? "high" : "medium";
  } else if (header.leafCandidates.length > 1) {
    // TOC-style page listing several sibling sections
    const leafSections = header.leafCandidates
      .map((code) => findSectionByCode(code))
      .filter((s): s is EctdSection => !!s);
    const fromFilename = filenameCodeFromScope(file.fileName, leafSections);
    if (fromFilename) {
      sectionCode = fromFilename;
      matchedBy = "filename";
      confidence = "high";
      candidates.add(fromFilename);
    } else if (folderCode && header.leafCandidates.includes(folderCode)) {
      sectionCode = folderCode;
      matchedBy = "folder";
      confidence = "high";
    } else {
      sectionCode = header.group;
      matchedBy = "header";
      confidence = "medium";
    }
  } else if (header.group && !header.weakOnly) {
    // Only a group-level code in the header; try refining with hints
    if (folderCode && isCodeDescendant(folderCode, header.group)) {
      sectionCode = folderCode;
      matchedBy = "folder";
      confidence = "high";
    } else {
      const scope = groupSection ? sectionAndDescendants(groupSection) : [];
      const fromFilename = filenameCodeFromScope(
        file.fileName,
        scope.filter((s) => s.code !== header.group)
      );
      if (fromFilename) {
        sectionCode = fromFilename;
        matchedBy = "filename";
        confidence = "medium";
        candidates.add(fromFilename);
      } else {
        sectionCode = header.group;
        matchedBy = "header";
        // A childless group section (3.2.R, 3.3, 3.2.P.2, ...) is exact
        confidence =
          groupSection && groupSection.childKeys.length === 0
            ? "high"
            : "medium";
      }
    }
  } else if (folderCode) {
    // No usable header (missing or cross-references only). The folder gives
    // the section; the filename can still pick a subsection within it.
    const fromFilename = filenameCodeFromScope(
      file.fileName,
      descendantsOfCode(folderCode)
    );
    if (fromFilename) {
      sectionCode = fromFilename;
      matchedBy = "filename";
      candidates.add(fromFilename);
    } else {
      sectionCode = folderCode;
      matchedBy = "folder";
    }
    confidence = "medium";
  } else if (header.group && header.weakOnly) {
    const fromFilename = filenameCodeFromScope(
      file.fileName,
      descendantsOfCode(header.group)
    );
    if (fromFilename) {
      sectionCode = fromFilename;
      matchedBy = "filename";
      candidates.add(fromFilename);
    } else {
      sectionCode = header.group;
      matchedBy = "header";
    }
    confidence = "low";
  } else {
    const fromFilename = filenameCodeFromScope(
      file.fileName,
      ectdSections.filter((s) => s.module === 3)
    );
    if (fromFilename) {
      sectionCode = fromFilename;
      matchedBy = "filename";
      confidence = "low";
      candidates.add(fromFilename);
    }
  }

  const section = sectionCode ? findSectionByCode(sectionCode) : undefined;
  const declaredNotApplicable = sectionCode
    ? detectNotApplicable(pageText, header.group ? header.groupOffset : 0)
    : false;

  return {
    file,
    sectionCode,
    section,
    variant: section ? detectVariant(file, section) : undefined,
    confidence,
    matchedBy,
    candidates: Array.from(candidates),
    declaredNotApplicable,
  };
}

/** Deterministic matching (header scan + folder/filename hints); no network. */
export function matchLoosePdfsToEctd(files: ParsedPdf[]): EctdFileMatch[] {
  return files.map(matchSingleFile);
}

/** Replace the match of one file with a manual user override. */
export function applyManualOverride(
  matches: EctdFileMatch[],
  fileName: string,
  sectionKey: string | null
): EctdFileMatch[] {
  return matches.map((match) => {
    if (match.file.fileName !== fileName) return match;
    const section = sectionKey ? findSectionByKey(sectionKey) : undefined;
    return {
      ...match,
      sectionCode: section ? section.code : null,
      section,
      variant: section ? detectVariant(match.file, section) : undefined,
      matchedBy: "manual",
      confidence: section ? "high" : "low",
    };
  });
}

// ---------------------------------------------------------------------------
// LLM fallback classification (batched)
// ---------------------------------------------------------------------------

const LLM_EXCERPT_CHARS = 3000;

type LlmClassification = {
  fileName?: string;
  code?: string | null;
  variant?: string | null;
};

function extractJsonArray(text: string): LlmClassification[] | null {
  const start = text.indexOf("[");
  const end = text.lastIndexOf("]");
  if (start === -1 || end <= start) return null;
  try {
    const parsed = JSON.parse(text.slice(start, end + 1));
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * True when the deterministic pass did not settle on a definite section:
 * nothing matched, low confidence, or a non-high-confidence hit on a container
 * section (the file most likely belongs to one of its subsections).
 */
export function needsLlmClassification(match: EctdFileMatch): boolean {
  if (!match.sectionCode) return true;
  if (match.confidence === "low") return true;
  if (match.matchedBy === "manual") return false;
  return (
    match.confidence !== "high" &&
    !!match.section &&
    match.section.childKeys.length > 0
  );
}

/**
 * Classify files that stayed unresolved (or low-confidence) after the
 * deterministic pass, using one batched OpenRouter call. Returns an updated
 * copy of `matches`; on any failure the original matches are returned.
 */
export async function classifyUnmatchedWithLlm(
  matches: EctdFileMatch[],
  options: RunPromptOptions = {}
): Promise<EctdFileMatch[]> {
  const unresolved = matches.filter(needsLlmClassification);
  if (unresolved.length === 0) return matches;

  const sectionList = ectdSections
    .filter((s) => s.module === 3)
    .map((s) => `${s.code} — ${s.title}`)
    .join("\n");

  const fileBlocks = unresolved
    .map((m) => {
      const excerpt = getFirstPageText(m.file).slice(0, LLM_EXCERPT_CHARS);
      return `--- fileName: "${m.file.fileName}"\nFirst page text:\n${excerpt}`;
    })
    .join("\n\n");

  const userContent = `You are given the first pages of PDF documents from a pharmaceutical dossier that follows the CTD Module 3 structure but is not packaged as eCTD. Assign each file to exactly one eCTD section code from the list below (or null if impossible). If the document names a manufacturer/controller (e.g. "Controlled by X"), report it as "variant".

Known section codes:
${sectionList}

Files:
${fileBlocks}

Respond with ONLY a JSON array, one item per file, in the same order:
[{"fileName": "...", "code": "3.2.S.4.2" | null, "variant": "..." | null}]`;

  try {
    const result = await runChatOpenRouter(
      [
        {
          role: "system",
          content:
            "You are a precise assistant for regulatory (eCTD/CTD) document classification. Reply with strict JSON only.",
        },
        { role: "user", content: userContent },
      ],
      options
    );

    const classifications = extractJsonArray(result.text);
    if (!classifications) return matches;

    const byFileName = new Map<string, LlmClassification>();
    classifications.forEach((c) => {
      if (c && typeof c.fileName === "string") byFileName.set(c.fileName, c);
    });

    return matches.map((match) => {
      if (!needsLlmClassification(match)) return match;
      const classification = byFileName.get(match.file.fileName);
      const normalized =
        classification && classification.code
          ? normalizeSectionCode(classification.code)
          : null;
      if (!normalized) return match;
      const section = findSectionByCode(normalized);
      if (!section) return match;
      const variant =
        getVariantRoot(section) === null
          ? undefined
          : (classification && classification.variant) ||
            detectVariant(match.file, section) ||
            "";
      return {
        ...match,
        sectionCode: normalized,
        section,
        variant,
        matchedBy: "llm" as MatchedBy,
        confidence: "medium" as MatchConfidence,
        candidates: Array.from(new Set([...match.candidates, normalized])),
      };
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("classifyUnmatchedWithLlm failed:", e);
    return matches;
  }
}

// ---------------------------------------------------------------------------
// Completeness report
// ---------------------------------------------------------------------------

const variantIdentity = (rootKey: string, name: string): string =>
  `${rootKey}::${name}`;

export function variantLabel(root: EctdSection, name: string): string {
  return `${root.title} — ${name || "(default)"}`;
}

type StatusResolverInput = {
  /** Files of this table (variant column or shared column) by section key. */
  filesBySectionKey: Map<string, EctdFileMatch[]>;
  /** Restricts which child sections belong to this table. */
  inScope: (section: EctdSection) => boolean;
};

/**
 * Build a memoised, bottom-up status resolver for one completeness table.
 *
 * - A section with files of its own is "present", or "not-applicable" when
 *   every file declares the section not applicable.
 * - A container without files takes its status from its in-scope children:
 *   any present child -> present; all children not-applicable -> not-applicable.
 * - Otherwise it is "not-applicable" when an ancestor declared N/A (the
 *   declaration flows down to every subsection), else "missing".
 */
function createStatusResolver({
  filesBySectionKey,
  inScope,
}: StatusResolverInput): (section: EctdSection) => SectionRowStatus {
  const naDeclaredKeys = new Set<string>();
  filesBySectionKey.forEach((files, key) => {
    if (files.length > 0 && files.every((f) => f.declaredNotApplicable)) {
      naDeclaredKeys.add(key);
    }
  });
  const memo = new Map<string, SectionRowStatus>();

  const ancestorNotApplicable = (section: EctdSection): boolean =>
    naDeclaredKeys.size > 0 &&
    getAncestors(section).some((ancestor) => naDeclaredKeys.has(ancestor.key));

  const statusOf = (section: EctdSection): SectionRowStatus => {
    const cached = memo.get(section.key);
    if (cached) return cached;

    let status: SectionRowStatus;
    const files = filesBySectionKey.get(section.key) || [];
    if (files.length > 0) {
      status = files.every((f) => f.declaredNotApplicable)
        ? "not-applicable"
        : "present";
    } else {
      const childStatuses = section.childKeys
        .map((key) => findSectionByKey(key))
        .filter((child): child is EctdSection => !!child && inScope(child))
        .map(statusOf);
      if (childStatuses.some((s) => s === "present")) {
        status = "present";
      } else if (
        childStatuses.length > 0 &&
        childStatuses.every((s) => s === "not-applicable")
      ) {
        status = "not-applicable";
      } else {
        status = ancestorNotApplicable(section) ? "not-applicable" : "missing";
      }
    }

    memo.set(section.key, status);
    return status;
  };

  return statusOf;
}

export function buildCompletenessReport(
  matches: EctdFileMatch[]
): EctdCompletenessReport {
  const matched = matches.filter(
    (m): m is EctdFileMatch & { section: EctdSection } => !!m.section
  );
  const unmatched = matches.filter((m) => !m.section);

  const detectedModules = Array.from(
    new Set(matched.map((m) => m.section.module))
  ).sort();

  const variants: VariantCoverage[] = [];

  // --- Variant columns (per repeatable root instance)
  const variantGroups = new Map<
    string,
    { root: EctdSection; name: string; files: typeof matched }
  >();
  matched.forEach((m) => {
    const root = getVariantRoot(m.section);
    if (!root) return;
    const name = m.variant || "";
    const id = variantIdentity(root.key, name);
    const existing = variantGroups.get(id);
    if (existing) {
      existing.files.push(m);
    } else {
      variantGroups.set(id, { root, name, files: [m] });
    }
  });

  Array.from(variantGroups.values())
    .sort(
      (a, b) =>
        a.root.key.localeCompare(b.root.key) || a.name.localeCompare(b.name)
    )
    .forEach(({ root, name, files }) => {
      // Sections whose variant root is this root. Nested repeatable subtrees
      // (3.2.P.4.x excipients) resolve to the enclosing drug product root, so
      // they are listed here alongside the other 3.2.P.x sections.
      const rowSections = ectdSections.filter(
        (s) =>
          s.key !== root.key &&
          isDescendantOf(s, root) &&
          getVariantRoot(s)?.key === root.key
      );

      const filesBySectionKey = new Map<string, EctdFileMatch[]>();
      files.forEach((m) => {
        const list = filesBySectionKey.get(m.section.key) || [];
        list.push(m);
        filesBySectionKey.set(m.section.key, list);
      });

      const statusOf = createStatusResolver({
        filesBySectionKey,
        inScope: (s) => getVariantRoot(s)?.key === root.key,
      });

      const rows: SectionRow[] = [];
      // Row for the root itself only when files were matched directly to it.
      const rootFiles = filesBySectionKey.get(root.key) || [];
      if (rootFiles.length > 0) {
        rows.push({
          section: root,
          files: rootFiles,
          status: statusOf(root),
        });
      }
      rowSections.forEach((section) => {
        rows.push({
          section,
          files: filesBySectionKey.get(section.key) || [],
          status: statusOf(section),
        });
      });

      variants.push({
        variantRootKey: root.key,
        name: variantLabel(root, name),
        rows,
      });
    });

  // --- Shared column: sections of detected modules without a variant root,
  // plus repeatable roots that have no matched instance at all (so completely
  // absent parts like an entirely missing 3.2.P are still reported).
  if (detectedModules.length > 0) {
    const rootsWithColumns = new Set(
      Array.from(variantGroups.values()).map((g) => g.root.key)
    );
    const sharedSections = ectdSections.filter((s) => {
      if (!detectedModules.includes(s.module)) return false;
      if (s.parentKey === null) return false;
      if (s.depth <= 1 && s.childKeys.length > 0) return false;
      const root = getVariantRoot(s);
      if (root === null) return true;
      // Orphan repeatable root without any variant column
      return root.key === s.key && !rootsWithColumns.has(s.key);
    });

    const sharedFiles = matched.filter(
      (m) => getVariantRoot(m.section) === null
    );
    const filesBySectionKey = new Map<string, EctdFileMatch[]>();
    sharedFiles.forEach((m) => {
      const list = filesBySectionKey.get(m.section.key) || [];
      list.push(m);
      filesBySectionKey.set(m.section.key, list);
    });
    const statusOf = createStatusResolver({
      filesBySectionKey,
      inScope: (s) => getVariantRoot(s) === null,
    });

    const rows: SectionRow[] = sharedSections.map((section) => ({
      section,
      files: filesBySectionKey.get(section.key) || [],
      status: statusOf(section),
    }));

    if (rows.length > 0) {
      variants.push({
        variantRootKey: null,
        name: "Common (non-repeatable sections)",
        rows,
      });
    }
  }

  return { variants, unmatched, detectedModules };
}
